import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Package, ShoppingBag, LogOut, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BRANDING } from "@/branding";

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login", { replace: true });
    }
  }, [loading, user, navigate]);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return null;

  const linkCls = ({ isActive }) =>
    `pill-btn flex items-center gap-2 px-4 h-10 rounded-full text-sm ${
      isActive ? "bg-black text-white" : "text-neutral-700 hover:bg-neutral-100"
    }`;

  const onSignOut = async () => { await logout(); navigate("/admin/login"); };

  const Nav = () => (
    <>
      <div>
        <Link to="/" className="font-serif-display text-xl">{BRANDING.storeName}</Link>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Admin</div>
        <nav className="mt-10 space-y-1">
          <NavLink to="/admin" end className={linkCls} onClick={() => setMobileOpen(false)} data-testid="nav-admin-home"><LayoutDashboard className="h-4 w-4" /> Dashboard</NavLink>
          <NavLink to="/admin/products" className={linkCls} onClick={() => setMobileOpen(false)} data-testid="nav-admin-products"><Package className="h-4 w-4" /> Products</NavLink>
          <NavLink to="/admin/orders" className={linkCls} onClick={() => setMobileOpen(false)} data-testid="nav-admin-orders"><ShoppingBag className="h-4 w-4" /> Orders</NavLink>
        </nav>
      </div>
      <div className="space-y-3">
        <div className="text-xs text-neutral-500">
          Signed in as<br/><span className="text-neutral-800 break-all">{user.email}</span>
        </div>
        <button
          onClick={onSignOut}
          className="pill-btn w-full h-10 rounded-full border border-black/15 text-sm inline-flex items-center justify-center gap-2 hover:bg-neutral-100"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] lg:flex" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 glass-header flex items-center justify-between h-14 px-4" data-testid="admin-mobile-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-black/10 hover:bg-neutral-100"
          aria-label="Open menu"
          data-testid="admin-menu-btn"
        >
          <Menu className="h-4 w-4" />
        </button>
        <Link to="/admin" className="font-serif-display text-lg">{BRANDING.storeName}</Link>
        <div className="h-10 w-10" />
      </header>

      {/* Mobile sheet nav */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 bg-white p-6 flex flex-col justify-between" data-testid="admin-mobile-nav">
          <SheetHeader>
            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
            <SheetDescription className="sr-only">Switch between admin sections.</SheetDescription>
          </SheetHeader>
          <Nav />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-black/10 bg-white p-6 flex-col justify-between min-h-screen">
        <Nav />
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
